// The Hand off dialog. BUILD-046 correction cycle 1bb88b3e (stage B2b, item N3) made it compact; its BEHAVIOR is unchanged (same destinations, same people
// filter, same request - POST /work/{id}/handoff with the legacy HandoffRequest - same default next action, same permission checks by the server).
//
// What changed is how it is drawn: the destination is a dropdown (a name each, with no repeated description of familiar departments) beside the person, the
// next action is one concise line, the note is optional, and ONE short line says what a handoff does ("Handoff completes this item and creates another. It is
// not reassignment."). The confirm button names the outcome: "Complete and hand off". A source-governed task keeps its restriction: it completes ONLY through
// its source, so the dialog explains that instead of showing a working form (the server also rejects it). All rules are in pages/sharedTasks/handoffModel.ts
// (tested); the focus trap is dialogFocusModel.ts. Escape and Cancel close it, and focus returns to the control that opened it.
import { useEffect, useRef, useState } from 'react';
import { handoffWorkItem, WorkItem, setTokenProvider } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import { attemptHandoff, describeHandoff, handoffFailed, handoffOffer, initialHandoff, selectDestination, selectPerson, setNextAction, setNotes } from '../pages/sharedTasks/handoffModel';
import type { HandoffState, HandoffUser } from '../pages/sharedTasks/handoffModel';
import { useDialogFocus } from '../pages/sharedTasks/useDialogFocus';
// Display only: the task title in the heading is shown without the "(FICTIONAL)" test marker; the request still carries the stored title.
import { stripTestMarker } from '../pages/sharedTasks/displayNames';

interface HandoffModalProps {
  item: WorkItem;
  onClose: () => void;
  onComplete: () => void;
}

const FIELD = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4AABDE] disabled:bg-gray-50 disabled:text-gray-500';

export function HandoffModal({ item, onClose, onComplete }: HandoffModalProps) {
  const { getToken } = useAuth();
  const [state, setState] = useState<HandoffState>(initialHandoff());
  const latest = useRef<HandoffState>(state);
  const [users, setUsers] = useState<HandoffUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const escapeRef = useRef<() => void>(() => undefined);
  const focus = useDialogFocus(true, dialogRef, () => escapeRef.current());
  const offer = handoffOffer(item as unknown as { sourceGoverned?: unknown });

  function publish(next: HandoffState): void {
    latest.current = next;
    setState(next);
  }

  // Load users when the dialog opens (only a working form needs them)
  useEffect(() => {
    setTokenProvider(getToken);
    if (offer.kind === 'form') void loadUsers();
    focus.focusFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadUsers() {
    setLoadingUsers(true);
    try {
      const token = await getToken();
      const response = await fetch(`${config.apiUrl}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }

  const view = describeHandoff(state, users, loadingUsers);
  const sending = state.phase === 'sending';

  function close(): void {
    if (latest.current.phase === 'sending') return;
    onClose();
  }
  escapeRef.current = close;

  // The confirm button is disabled while the request runs, which drops focus onto the page; after a failure focus returns to it.
  const failed = state.phase === 'failed';
  useEffect(() => {
    if (failed) focus.focusId('handoff-confirm');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const step = attemptHandoff(latest.current, item, users);
    publish(step.state);
    if (step.request === null) return;
    try {
      await handoffWorkItem(item.id, step.request);
      onComplete();
    } catch (err: unknown) {
      publish(handoffFailed(latest.current, err));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-2 sm:items-center sm:p-6">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="handoff-heading" tabIndex={-1} className="flex max-h-[calc(100vh-1rem)] w-full max-w-xl flex-col rounded-lg bg-white shadow-xl focus:outline-none sm:max-h-[calc(100vh-3rem)]" data-testid="handoff-dialog">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 px-5 py-3">
          <h3 id="handoff-heading" className="text-lg font-medium text-gray-900">
            Hand off task
          </h3>
          <p className="mt-0.5 truncate text-sm text-gray-600" title={stripTestMarker(item.title)}>
            {stripTestMarker(item.title)}
          </p>
        </div>

        {offer.kind === 'governed' ? (
          <div className="space-y-3 px-5 py-4" data-testid="handoff-governed">
            <p role="status" className="text-sm text-gray-800">
              {offer.text}
            </p>
            <div className="flex justify-end">
              <button type="button" data-autofocus="" onClick={close} className="rounded-md bg-[#4AABDE] px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#4AABDE]">
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Destination: a name each, nothing repeated */}
                <div>
                  <label htmlFor="handoff-destination" className="mb-1 block text-sm font-medium text-gray-700">
                    Hand off to
                  </label>
                  <select id="handoff-destination" data-autofocus="" value={state.role} disabled={sending} onChange={(e) => publish(selectDestination(latest.current, e.target.value))} className={FIELD}>
                    <option value="">Choose a destination…</option>
                    {view.destinations.map((d) => (
                      <option key={d.role} value={d.role}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Person */}
                <div>
                  <label htmlFor="handoff-person" className="mb-1 block text-sm font-medium text-gray-700">
                    Person
                  </label>
                  {view.people.kind === 'list' ? (
                    <select id="handoff-person" value={view.people.selected} disabled={sending} onChange={(e) => publish(selectPerson(latest.current, e.target.value))} className={FIELD}>
                      <option value="">Select a person…</option>
                      {view.people.options.map((o) => (
                        <option key={o.value} value={o.value} title={o.title}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <select id="handoff-person" disabled aria-describedby="handoff-person-note" value="" onChange={() => undefined} className={FIELD}>
                        <option value="">{view.people.kind === 'none' ? 'Choose a destination first' : 'No one to choose'}</option>
                      </select>
                      {view.people.kind !== 'none' && (
                        <p id="handoff-person-note" role="status" className="mt-1 text-xs text-gray-600">
                          {view.people.text}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="handoff-next" className="mb-1 block text-sm font-medium text-gray-700">
                  Next action for the recipient
                </label>
                <input id="handoff-next" type="text" value={state.nextAction} disabled={sending} placeholder="Review and accept handoff" onChange={(e) => publish(setNextAction(latest.current, e.target.value))} className={FIELD} />
              </div>

              <div>
                <label htmlFor="handoff-notes" className="mb-1 block text-sm font-medium text-gray-700">
                  Note (optional)
                </label>
                <textarea id="handoff-notes" rows={2} value={state.notes} disabled={sending} onChange={(e) => publish(setNotes(latest.current, e.target.value))} className={FIELD} />
              </div>

              <p className="text-xs text-gray-600" data-testid="handoff-consequence">
                {view.consequenceLine}
              </p>

              {view.error !== null && (
                <p role="alert" className="text-sm text-red-700" data-testid="handoff-error">
                  {view.error}
                </p>
              )}
            </div>

            {/* Actions: pinned */}
            <div className="flex flex-shrink-0 justify-end gap-3 border-t border-gray-200 px-5 py-3">
              <button type="button" onClick={close} disabled={sending} className="rounded px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50">
                Cancel
              </button>
              <button id="handoff-confirm" type="submit" disabled={view.submit.disabled} className="rounded-md bg-[#4AABDE] px-4 py-2 text-sm font-medium text-white hover:bg-[#3a9bce] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#4AABDE] disabled:opacity-50">
                {view.submit.label}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
